import { motion } from 'framer-motion';
import { Play, Shield, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TranslatedText } from '@/components/TranslatedText';
import { DottedSurface } from '@/components/ui/dotted-surface';
import { Link } from 'react-router-dom';
import { trackCTAClick } from '@/lib/metaPixel';

const trustSteps = ['Learn', 'Build', 'Automate', 'Monetize'];

export const LightHeroSection = () => {
  return (
    <section id="hero" className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 bg-background overflow-hidden">
      <DottedSurface />
      <div className="relative z-10 max-w-2xl mx-auto text-center">
        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-[28px] leading-[1.12] sm:text-4xl md:text-5xl lg:text-[56px] font-bold text-foreground tracking-tight"
        >
          <TranslatedText>Build Faceless Online Projects With AI</TranslatedText>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-5 sm:mt-7 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto"
        >
          <TranslatedText>Everything you need in one place — structured lessons, built-in AI tools, and a personal AI teacher to guide you step by step.</TranslatedText>
        </motion.p>

        {/* Trust line */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="mt-6 flex items-center justify-center gap-2 sm:gap-3"
        >
          {trustSteps.map((step, i) => (
            <span key={step} className="flex items-center gap-2 sm:gap-3">
              <span className="text-sm sm:text-base font-semibold text-foreground tracking-wide">
                <TranslatedText>{step}</TranslatedText>
              </span>
              {i < trustSteps.length - 1 && (
                <ArrowRight className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              )}
            </span>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-9 sm:mt-10 flex flex-col items-center"
        >
          <Link to="/apply?plan=free" onClick={() => trackCTAClick('Start With 1 Free Class', 'Hero')}>
            <Button
              size="lg"
              className="w-full sm:w-auto bg-primary hover:bg-primary/90 active:scale-[0.98] text-primary-foreground text-base font-semibold h-14 rounded-xl shadow-lg shadow-primary/20 transition-all px-10"
            >
              <Play className="w-5 h-5 mr-2" />
              <TranslatedText>Start With 1 Free Class</TranslatedText>
            </Button>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.55 }}
          className="flex items-center justify-center gap-2 mt-4 text-xs sm:text-sm text-muted-foreground"
        >
          <Shield className="w-4 h-4 text-green-500 flex-shrink-0" />
          <span><TranslatedText>No credit card required</TranslatedText></span>
        </motion.div>
      </div>
    </section>
  );
};

export default LightHeroSection;
